import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { readAuth, writeAuth, clearAuth } from './storage';
import { extractDepts } from './jwt';
import { onTokenUpdated, onLoggedOut } from './events';
import { can, hasRole, hasDept, isSuperadmin, isAdmin, normalizeRole } from './permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readAuth());

  useEffect(() => {
    const offToken = onTokenUpdated(({ token, depts }) => {
      setUser((prev) => (prev ? { ...prev, token, depts } : prev));
    });
    const offLogout = onLoggedOut(() => setUser(null));
    const onStorage = (e) => {
      if (!e.key) {
        setUser(readAuth());
        return;
      }
      if (['authToken', 'role', 'depts', 'username'].includes(e.key)) {
        setUser(readAuth());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      offToken();
      offLogout();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const login = useCallback(({ token, tokenType, username, role, id }) => {
    const depts = extractDepts(token);
    const next = {
      token,
      tokenType: tokenType || 'Bearer',
      username,
      role: normalizeRole(role),
      depts,
      id: id ?? null,
    };
    writeAuth(next);
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      userId: user?.id || null,
      isAuthenticated: !!user?.token,
      username: user?.username || '',
      role: user?.role || null,
      depts: user?.depts || [],
      login,
      logout,
      hasRole: (r) => hasRole(user, r),
      hasDept: (d) => hasDept(user, d),
      can: (req) => can(user, req),
      isSuperadmin: isSuperadmin(user),
      isAdmin: isAdmin(user),
    }),
    [user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
